export type Kostenpost = {
  post: string;
  van: number;
  tot: number;
  detail: string;
};

export type Dienst = {
  slug: string;
  naam: string;
  h1: string;
  prijs: string;
  /** ondergrens van de totale klus in euro */
  van: number;
  /** bovengrens van de totale klus in euro */
  tot: number;
  /** meest voorkomende totaalprijs in euro */
  gemiddeld: number;
  /** gangbaar uurtarief van een erkende elektricien voor dit werk */
  uurtarief: string;
  /** hoeveel tijd het werk gemiddeld kost */
  doorlooptijd: string;
  kosten: Kostenpost[];
  kort: string;
  intro: string;
  werk: string[];
  faq: { v: string; a: string; link?: { to: "/netbeheerders"; label: string } }[];
};

export const euro = (bedrag: number) =>
  `€ ${bedrag.toLocaleString("nl-NL", { maximumFractionDigits: 0 })}`;

export const bereik = (van: number, tot: number) => `${euro(van)} tot ${euro(tot)}`;

export const diensten: Dienst[] = [
  {
    slug: "storing-verhelpen",
    naam: "Elektra storingen oplossen",
    h1: "Elektricien voor storingen",
    prijs: "€ 80 tot € 250",
    van: 80,
    tot: 250,
    gemiddeld: 145,
    uurtarief: "€ 55 tot € 85 per uur",
    doorlooptijd: "1 tot 3 uur",
    kosten: [
      { post: "Voorrijkosten", van: 25, tot: 55, detail: "eenmalig, binnen de eigen regio vaak lager" },
      { post: "Storing opsporen (eerste uur)", van: 55, tot: 85, detail: "doormeten van groepen en aardlek" },
      { post: "Vervolguur", van: 50, tot: 80, detail: "bij lastig te vinden fouten in oude bedrading" },
      { post: "Stopcontact of schakelaar vervangen", van: 15, tot: 45, detail: "materiaal plus montage" },
      { post: "Aardlekschakelaar vervangen", van: 95, tot: 175, detail: "inclusief nieuwe automaat en test" },
      { post: "Spoedtoeslag avond of weekend", van: 45, tot: 120, detail: "alleen bij een spoedbezoek" },
    ],
    kort: "Kortsluiting, een groep die uitvalt of geen stroom in een deel van de woning.",
    intro:
      "Een elektrische storing legt vaak een half huis stil: de aardlekschakelaar slaat eruit, een groep valt telkens uit of een stopcontact doet het niet meer. Een erkende elektricien meet de installatie door, spoort de fout op en lost hem meestal binnen één bezoek op.",
    werk: [
      "Doormeten van groepen en aardlekschakelaars",
      "Opsporen van kortsluiting en aardlekken",
      "Vervangen van defecte schakelaars, stopcontacten en bedrading",
      "Controle van de meterkast na de reparatie",
    ],
    faq: [
      {
        v: "Hoe snel kan een elektricien bij een storing komen?",
        a: "Bij een spoedstoring komen aangesloten elektriciens vaak nog dezelfde dag; bij niet-urgente storingen meestal binnen enkele werkdagen.",
      },
      {
        v: "Wat kost het opsporen van een storing?",
        a: "Reken op € 80 tot € 250 inclusief voorrijkosten en het eerste uur. Complexe fouten in oude bedrading kosten meer tijd.",
      },
    ],
  },
  {
    slug: "krachtstroom-aanleggen",
    naam: "Krachtstroom aanleggen",
    h1: "Krachtstroom aanleggen (400 volt)",
    prijs: "€ 350 tot € 900",
    van: 350,
    tot: 900,
    gemiddeld: 575,
    uurtarief: "€ 55 tot € 85 per uur",
    doorlooptijd: "halve tot hele dag",
    kosten: [
      { post: "Voorrijkosten en inspectie", van: 45, tot: 75, detail: "bepalen van route en belasting" },
      { post: "Arbeidsloon per uur", van: 55, tot: 85, detail: "gemiddeld 4 tot 8 uur werk" },
      { post: "Krachtstroomgroep in de kast", van: 120, tot: 260, detail: "automaat en aardlekbeveiliging" },
      { post: "Kabel 5-aderig per meter", van: 6, tot: 14, detail: "dikte afhankelijk van vermogen" },
      { post: "Krachtstroomcontactdoos (CEE)", van: 45, tot: 120, detail: "16 of 32 ampère, binnen of buiten" },
    ],
    kort: "Een 400 volt aansluiting voor inductie, werkplaats, sauna of machines.",
    intro:
      "Zware apparatuur zoals een inductiefornuis, kookgroep, sauna, lasapparaat of machine in de werkplaats vraagt om krachtstroom. Een erkende elektricien berekent de benodigde groep, legt de kabel aan en levert de installatie veilig en volgens NEN 1010 op.",
    werk: [
      "Berekenen van het benodigde vermogen en de kabeldikte",
      "Plaatsen van een krachtstroomgroep in de groepenkast",
      "Aanleggen van 5-aderige kabel naar keuken, garage, schuur of werkplaats",
      "Monteren van CEE contactdozen 16 of 32 ampère",
      "Meten, testen en opleveren volgens NEN 1010",
    ],
    faq: [
      {
        v: "Wat kost het aanleggen van krachtstroom?",
        a: "Reken op € 350 tot € 900. Een korte aansluiting vanaf de meterkast in de garage blijft vaak onder € 500; een lange kabelroute naar een schuur of werkplaats loopt op naar € 900 of meer.",
      },
      {
        v: "Heb ik altijd krachtstroom nodig voor een inductiekookplaat?",
        a: "Niet altijd. Veel kookplaten werken op twee zware enkelfasegroepen. Bij een fornuis boven 7,4 kilowatt of een professionele kookplaat is 400 volt meestal wel nodig.",
      },
      {
        v: "Kan krachtstroom in elke woning?",
        a: "Alleen als de woning een 3-fase aansluiting heeft. Is dat niet zo, dan vraagt de elektricien een verzwaring aan bij de netbeheerder; die kosten komen er los bij.",
      },
    ],
  },
  {
    slug: "perilex-aansluiten",
    naam: "Perilex aansluiten",
    h1: "Perilex aansluiten voor fornuis of kookplaat",
    prijs: "€ 150 tot € 400",
    van: 150,
    tot: 400,
    gemiddeld: 245,
    uurtarief: "€ 55 tot € 85 per uur",
    doorlooptijd: "1 tot 3 uur",
    kosten: [
      { post: "Voorrijkosten", van: 25, tot: 55, detail: "eenmalig, binnen de eigen regio vaak lager" },
      { post: "Arbeidsloon per uur", van: 55, tot: 85, detail: "gemiddeld 1 tot 3 uur werk" },
      { post: "Perilex contactdoos met stekker", van: 25, tot: 60, detail: "inbouw of opbouw, inclusief afdekplaat" },
      { post: "Kabel 5-aderig per meter", van: 5, tot: 12, detail: "meestal 2,5 mm2 vanaf de meterkast" },
      { post: "Extra groep in de meterkast", van: 140, tot: 260, detail: "alleen als er geen vrije zware groep is" },
    ],
    kort: "Een perilex aansluiting voor een elektrisch fornuis of zware kookplaat.",
    intro:
      "Een elektrisch fornuis of een zware keramische kookplaat vraagt vaak om een perilex aansluiting. Dat is een vijfpolige contactdoos op twee fasen, zwaarder dan een gewoon stopcontact maar lichter dan volledige krachtstroom. Een erkende elektricien controleert de meterkast, trekt zo nodig een nieuwe kabel en monteert de perilex contactdoos veilig volgens NEN 1010.",
    werk: [
      "Controleren van de meterkast en de beschikbare groepen",
      "Aanleggen van 5-aderige kabel van de meterkast naar de keuken",
      "Plaatsen van een zware groep of aardlekautomaat waar nodig",
      "Monteren van de perilex contactdoos, inbouw of opbouw",
      "Aansluiten van de perilex stekker op fornuis of kookplaat",
      "Doormeten, testen en opleveren volgens NEN 1010",
    ],
    faq: [
      {
        v: "Wat kost het aansluiten van perilex?",
        a: "Reken op € 150 tot € 400. Zit er al een geschikte kabel en vrije groep in de keuken, dan blijft het vaak onder € 200. Moet er een nieuwe kabel en een extra groep bij, dan loopt het op naar € 400 of meer.",
      },
      {
        v: "Wat is het verschil tussen perilex en krachtstroom?",
        a: "Perilex gebruikt twee fasen en levert ongeveer 7,4 kilowatt, genoeg voor de meeste fornuizen en kookplaten. Krachtstroom gebruikt drie fasen op 400 volt en is bedoeld voor zwaardere apparatuur, een sauna of een werkplaats.",
      },
      {
        v: "Mag ik perilex zelf aansluiten?",
        a: "Het aansluiten van de stekker op een bestaande perilex contactdoos mag je vaak zelf. Het plaatsen van de contactdoos, de kabel en de groep in de meterkast is werk voor een erkende elektricien; anders vervalt bij schade je verzekering.",
      },
      {
        v: "Heb ik een aparte groep nodig voor perilex?",
        a: "Ja, een perilex aansluiting hoort op een eigen zware groep met aardlekbeveiliging. Is die er niet, dan plaatst de elektricien er een bij in de groepenkast.",
      },
    ],
  },
  {
    slug: "elektra-aanleggen",
    naam: "Elektra aanleggen",
    h1: "Elektra aanleggen en uitbreiden",
    prijs: "€ 90 tot € 180 per punt",
    van: 90,
    tot: 180,
    gemiddeld: 130,
    uurtarief: "€ 55 tot € 85 per uur",
    doorlooptijd: "een halve dag tot enkele dagen",
    kosten: [
      { post: "Extra stopcontact inbouw", van: 90, tot: 180, detail: "per punt, inclusief frezen en afmonteren" },
      { post: "Extra stopcontact opbouw", van: 55, tot: 110, detail: "geen breekwerk nodig" },
      { post: "Extra groep in de meterkast", van: 140, tot: 260, detail: "bijvoorbeeld voor keuken of oven" },
      { post: "Krachtstroomgroep 400V", van: 275, tot: 550, detail: "voor inductie, sauna of werkplaats" },
      { post: "Woning herbedraden per m2", van: 55, tot: 95, detail: "rijtjeswoning van 100 m2: 5.500 tot 9.500 euro" },
      { post: "Buitenaansluiting schuur of tuin", van: 225, tot: 650, detail: "inclusief grondwerk en kabel" },
    ],
    kort: "Stopcontacten bijplaatsen, leidingwerk trekken of een woning herbedraden.",
    intro:
      "Bij een verbouwing, nieuwe keuken of uitbouw komt er bijna altijd elektra bij. Leidingwerk wordt volgens NEN 1010 aangelegd, weggefreesd in de muur of netjes in opbouw, en afgemonteerd op een eigen groep waar dat nodig is.",
    werk: [
      "Stopcontacten en schakelaars bijplaatsen",
      "Keuken-, badkamer- en tuinelektra aanleggen",
      "Complete woning herbedraden",
      "Leidingwerk frezen, trekken en afmonteren",
    ],
    faq: [
      {
        v: "Wat kost een extra stopcontact?",
        a: "Gemiddeld € 90 tot € 180 per punt. Meerdere punten tegelijk laten plaatsen scheelt flink in de prijs.",
      },
      {
        v: "Hoe lang duurt herbedraden?",
        a: "Een rijtjeswoning volledig herbedraden kost doorgaans drie tot vijf werkdagen.",
      },
    ],
  },
  {
    slug: "groepenkast",
    naam: "Groepenkast aanleggen of uitbreiden",
    h1: "Groepenkast vervangen of uitbreiden",
    prijs: "€ 650 tot € 1.400",
    van: 650,
    tot: 1400,
    gemiddeld: 975,
    uurtarief: "€ 55 tot € 85 per uur",
    doorlooptijd: "een halve tot hele werkdag",
    kosten: [
      { post: "Groepenkast 3 groepen, 1 aardlek", van: 550, tot: 800, detail: "klein appartement" },
      { post: "Groepenkast 6 groepen, 2 aardlek", van: 750, tot: 1150, detail: "meest gekozen bij een rijtjeswoning" },
      { post: "Groepenkast 9 tot 12 groepen", van: 1100, tot: 1900, detail: "ruime woning of 3-fase installatie" },
      { post: "Extra groep bijplaatsen", van: 140, tot: 260, detail: "in een bestaande kast met ruimte" },
      { post: "Overspanningsbeveiliging", van: 150, tot: 325, detail: "type 2, aanbevolen bij zonnepanelen" },
      { post: "Aarding aanleggen of verbeteren", van: 180, tot: 450, detail: "aardpen of aardleiding" },
      { post: "Arbeidsloon plaatsing", van: 275, tot: 500, detail: "4 tot 8 uur werk" },
    ],
    kort: "Een veilige, moderne meterkast met voldoende groepen en aardlekschakelaars.",
    intro:
      "Een oude groepenkast met stoppen of zonder aardlekschakelaar is niet meer veilig en biedt te weinig ruimte voor moderne apparatuur. Een nieuwe kast wordt volgens NEN 1010 opgebouwd, doorgemeten en met een opleveringsrapport aan je overgedragen.",
    werk: [
      "Vervangen van een verouderde groepenkast",
      "Uitbreiden met extra groepen voor keuken, laadpaal of zonnepanelen",
      "Plaatsen van aardlekschakelaars en overspanningsbeveiliging",
      "Aarding controleren en verbeteren",
    ],
    faq: [
      {
        v: "Wanneer moet mijn groepenkast vervangen worden?",
        a: "Bij stoppen in plaats van automaten, het ontbreken van een aardlekschakelaar, een kast ouder dan 25 jaar of te weinig groepen.",
      },
      {
        v: "Hoe lang zit ik zonder stroom?",
        a: "Het vervangen duurt meestal een halve tot hele werkdag; de stroom is daarvan enkele uren af.",
      },
    ],
  },
  {
    slug: "databekabeling",
    naam: "Netwerktechniek & UTP",
    h1: "Netwerktechniek en UTP aanleggen",
    prijs: "€ 150 tot € 900",
    van: 150,
    tot: 900,
    gemiddeld: 475,
    uurtarief: "€ 50 tot € 80 per uur",
    doorlooptijd: "2 uur tot een hele dag",
    kosten: [
      { post: "Netwerkpunt cat6", van: 100, tot: 200, detail: "per aansluitpunt, inclusief wandcontactdoos" },
      { post: "Netwerkpunt cat6a afgeschermd", van: 135, tot: 250, detail: "voor 10 Gbit of lange trajecten" },
      { post: "Patchkast met patchpaneel", van: 175, tot: 425, detail: "inclusief montage in de meterkast" },
      { post: "Accesspoint plaatsen", van: 145, tot: 325, detail: "inclusief voeding over de netwerkkabel" },
      { post: "Camerabekabeling per punt", van: 110, tot: 210, detail: "buitenpunt met doorvoer" },
      { post: "Arbeidsloon per uur", van: 50, tot: 80, detail: "bij los meerwerk" },
    ],
    kort: "Vaste internetaansluitingen, UTP-bekabeling en wifi die overal werkt.",
    intro:
      "Thuiswerken, streamen en camera's vragen om een stabiel netwerk. Een installateur trekt UTP-kabels (cat6 of cat6a) naar vaste netwerkpunten met nette wandcontactdozen, richt een patchkast met patchpaneel in bij de meterkast en plaatst wifi-accesspoints of een mesh-systeem voor dekking in elke kamer. Ook het doortrekken van glasvezel of het modem vanuit de meterkast naar een andere kamer en voeding over de netwerkkabel (PoE) voor camera's en accesspoints hoort daarbij.",
    werk: [
      "UTP cat6/cat6a trekken naar werkkamer, woonkamer en zolder, afgemonteerd op wandcontactdozen",
      "Netwerkkast met patchpaneel monteren, bijvoorbeeld in de meterkast",
      "Glasvezel of modem doortrekken van de meterkast naar een andere kamer",
      "Wifi-accesspoints of een mesh-systeem plaatsen voor dekking in de hele woning",
      "PoE-bekabeling voor camera's, accesspoints en domotica",
    ],
    faq: [
      {
        v: "Cat6 of Cat6a?",
        a: "Cat6 is voor de meeste woningen ruim voldoende (1 Gbit, op korte afstanden 10 Gbit). Cat6a is afgeschermd en geschikt voor 10 Gbit over langere afstanden, maar is duurder en lastiger te verwerken. De installateur adviseert per traject.",
      },
      {
        v: "Hoeveel kost een extra netwerkpunt?",
        a: "Reken op € 100 tot € 200 per aansluitpunt, afhankelijk van de afstand tot de meterkast en of er gefreesd moet worden. Meerdere punten tegelijk aanleggen is relatief goedkoper.",
      },
      {
        v: "Is een bekabeld netwerk beter dan wifi mesh?",
        a: "Voor vaste plekken zoals een werkkamer, tv of camera is een kabel altijd stabieler en sneller. Wifi mesh is een goede aanvulling voor telefoons en laptops; het mooiste resultaat combineert beide: bekabeling naar vaste punten en accesspoints voor draadloos.",
      },
      {
        v: "Kan een netwerkkabel in bestaande muren?",
        a: "Vaak wel: via bestaande leidingen, kabelgoten of langs de plint. Anders wordt er netjes gefreesd en weggewerkt.",
      },
    ],
  },
  {
    slug: "laadpaal-installeren",
    naam: "Laadpaal installeren",
    h1: "Laadpaal laten installeren",
    prijs: "€ 900 tot € 2.000",
    van: 900,
    tot: 2000,
    gemiddeld: 1395,
    uurtarief: "€ 60 tot € 90 per uur",
    doorlooptijd: "3 tot 6 uur",
    kosten: [
      { post: "Wallbox 11 kW", van: 550, tot: 1200, detail: "apparaat zelf, afhankelijk van merk" },
      { post: "Laadpaal op statief", van: 750, tot: 1600, detail: "vrijstaand op de oprit" },
      { post: "Installatie en montage", van: 325, tot: 650, detail: "3 tot 6 uur arbeid" },
      { post: "Aparte groep met type-A EV aardlek", van: 175, tot: 350, detail: "verplicht voor laden thuis" },
      { post: "Kabelwerk per meter", van: 12, tot: 28, detail: "meer bij graafwerk door tuin of oprit" },
      { post: "Verzwaring naar 3-fase", van: 350, tot: 900, detail: "exclusief kosten van de netbeheerder" },
      { post: "Load balancing module", van: 175, tot: 400, detail: "voorkomt overbelasting van de aansluiting" },
    ],
    kort: "Thuis laden met een eigen groep, kabelwerk en veilige oplevering.",
    intro:
      "Een laadpaal of wallbox hoort op een eigen groep met een geschikte aardlekbeveiliging. De installateur bekijkt de afstand tot de meterkast, controleert of je aansluiting het laadvermogen aankan en levert de installatie getest op.",
    werk: [
      "Wallbox of laadpaal monteren aan gevel of paal",
      "Aparte groep met type-B of type-A EV aardlekbeveiliging",
      "Kabelwerk door de tuin of oprit",
      "Load balancing en koppeling met zonnepanelen",
    ],
    faq: [
      {
        v: "Heb ik 3-fase nodig?",
        a: "Voor laden tot 3,7 kW volstaat 1-fase. Wil je sneller laden (11 kW of meer), dan is een 3-fase aansluiting nodig.",
        link: { to: "/netbeheerders", label: "Zo vraag je een 3-fase aansluiting aan →" },
      },
      {
        v: "Is subsidie mogelijk?",
        a: "Landelijke subsidie voor particulieren is er niet, maar sommige gemeenten en werkgevers vergoeden een deel van de installatie.",
      },
    ],
  },
  {
    slug: "verlichting-aanleggen",
    naam: "Verlichting aanleggen",
    h1: "Verlichting laten aanleggen",
    prijs: "€ 250 tot € 900",
    van: 250,
    tot: 900,
    gemiddeld: 520,
    uurtarief: "€ 50 tot € 80 per uur",
    doorlooptijd: "2 uur tot een hele dag",
    kosten: [
      { post: "Inbouwspot plaatsen", van: 45, tot: 90, detail: "per spot, inclusief bedrading" },
      { post: "Set van 6 spots in een plafond", van: 320, tot: 620, detail: "meest gevraagde opdracht" },
      { post: "Dimmer installeren", van: 60, tot: 130, detail: "geschikte led-dimmer inbegrepen" },
      { post: "Lichtpunt verplaatsen", van: 85, tot: 175, detail: "inclusief herstel van het plafond" },
      { post: "Buitenlamp met bewegingsmelder", van: 110, tot: 240, detail: "aan gevel of berging" },
      { post: "Tuinverlichting per armatuur", van: 95, tot: 210, detail: "inclusief grondkabel" },
    ],
    kort: "Inbouwspots, dimmers, buiten- en sfeerverlichting met een doordacht lichtplan.",
    intro:
      "Goede verlichting maakt een ruimte af. Van inbouwspots in het plafond en dimbare led tot buitenverlichting met schemerschakelaar: de elektricien maakt een lichtplan, trekt de bedrading en monteert alles strak af.",
    werk: [
      "Inbouwspots plaatsen in plafond of verlaagd plafond",
      "Dimmers en slimme schakelaars installeren",
      "Buiten- en tuinverlichting met bewegingsmelder",
      "Lichtplan voor keuken, woonkamer of bedrijfsruimte",
    ],
    faq: [
      {
        v: "Wat kost het plaatsen van inbouwspots?",
        a: "Gemiddeld € 45 tot € 90 per spot inclusief montage; in een bestaand plafond met beperkte ruimte ligt dat hoger.",
      },
      {
        v: "Kunnen alle ledlampen gedimd worden?",
        a: "Alleen dimbare led in combinatie met een geschikte led-dimmer; anders gaan lampen flikkeren of zoemen.",
      },
    ],
  },
  {
    slug: "camerabeveiliging",
    naam: "Camerabeveiliging installeren",
    h1: "Camerabeveiliging laten installeren",
    prijs: "€ 500 tot € 2.600",
    van: 500,
    tot: 2600,
    gemiddeld: 1250,
    uurtarief: "€ 55 tot € 85 per uur",
    doorlooptijd: "een halve tot hele dag",
    kosten: [
      { post: "Buitencamera", van: 100, tot: 400, detail: "per stuk, weerbestendig met nachtzicht" },
      { post: "Binnencamera", van: 75, tot: 250, detail: "per stuk, vaak compacter en stiller" },
      { post: "Recorder (NVR)", van: 200, tot: 500, detail: "lokale opslag van alle camerabeelden" },
      { post: "Installatie en bekabeling", van: 200, tot: 600, detail: "afhankelijk van aantal camera's en kabelroutes" },
      { post: "Cloudopslag", van: 5, tot: 15, detail: "per maand, als alternatief voor een recorder" },
    ],
    kort: "Binnen- en buitencamera's met opname op een recorder of in de cloud.",
    intro:
      "Met camerabeveiliging houd je zicht op je woning, oprit of schuur, ook als je er niet bent. Een installateur helpt je kiezen tussen bedrade camera's met voeding over de netwerkkabel (PoE) of draadloze modellen, bepaalt de beste posities en richt de opname in op een recorder of in de cloud. Je kijkt mee via een app op je telefoon.",
    werk: [
      "Bepalen van de beste cameraposities rond de woning",
      "Aanleggen van bekabeling of inrichten van draadloze camera's",
      "Plaatsen en instellen van een recorder of cloudopslag",
      "Inrichten van de app met meldingen bij beweging",
      "Advies over privacy en het juist richten van de camera's",
    ],
    faq: [
      {
        v: "Mag ik de straat filmen met mijn camera?",
        a: "Nee, dat mag in principe niet. Volgens de privacywet (AVG) richt je je camera alleen op je eigen terrein. De openbare weg, de tuin van de buren of hun voordeur mogen niet in beeld zijn. Lukt dat niet helemaal, dan moet je dat zo klein mogelijk houden en het met de buren bespreken. Een bordje of sticker dat er camerabeveiliging is, is verplicht voor bezoekers.",
      },
      {
        v: "Bedraad of draadloos?",
        a: "Bedrade camera's (PoE) krijgen stroom en beeld via één netwerkkabel en zijn het meest betrouwbaar, zeker buiten. Draadloze camera's zijn sneller geplaatst en handig waar kabels lastig zijn, maar hangen af van wifi-bereik en batterijen of een stopcontact.",
      },
      {
        v: "Hoeveel camera's heb ik nodig?",
        a: "Voor een eengezinswoning is één tot vijf camera's gebruikelijk: voordeur, achterdeur en eventueel oprit of schuur. De installateur kijkt mee welke hoeken echt nodig zijn, zodat je niet onnodig veel camera's betaalt.",
      },
      {
        v: "Waar worden de beelden opgeslagen?",
        a: "Op een recorder (NVR) in huis of in de cloud. Een recorder is een eenmalige aankoop zonder maandelijkse kosten; cloudopslag kost € 5 tot € 15 per maand maar blijft bewaard als de recorder zelf wordt meegenomen.",
      },
    ],
  },
  {
    slug: "inbraakbeveiliging",
    naam: "Inbraakbeveiliging (alarmsysteem)",
    h1: "Alarmsysteem laten installeren",
    prijs: "€ 800 tot € 2.700",
    van: 800,
    tot: 2700,
    gemiddeld: 1400,
    uurtarief: "€ 55 tot € 85 per uur",
    doorlooptijd: "een halve tot hele dag",
    kosten: [
      { post: "Draadloos alarmsysteem", van: 250, tot: 1500, detail: "materiaal, afhankelijk van aantal sensoren" },
      { post: "Bedraad alarmsysteem", van: 375, tot: 1700, detail: "materiaal, duurzamer maar meer montagewerk" },
      { post: "Installatie", van: 400, tot: 1000, detail: "plaatsen, koppelen en testen van het systeem" },
      { post: "Meldkamer-abonnement", van: 15, tot: 30, detail: "per maand, voor doormelding bij alarm" },
    ],
    kort: "Een alarmsysteem met sensoren, sirene en eventueel doormelding naar een meldkamer.",
    intro:
      "Een alarmsysteem schrikt inbrekers af en waarschuwt jou en je buren direct bij een inbraakpoging. De installateur kiest samen met jou een draadloos, bedraad of hybride systeem, plaatst deur- en raamsensoren, bewegingsmelders en een sirene, en koppelt alles aan een app. Op verzoek wordt het alarm doorgemeld naar een meldkamer.",
    werk: [
      "Risico-inventarisatie van de woning en toegangspunten",
      "Plaatsen van deur-, raam- en bewegingssensoren",
      "Monteren van bedieningspaneel, sirene en eventueel rookmelders",
      "Koppelen aan de app en eventueel de meldkamer",
      "Testen van het complete systeem en uitleg aan de bewoners",
    ],
    faq: [
      {
        v: "Heb ik een meldkamer nodig?",
        a: "Nee, het is geen verplichting. Zonder meldkamer krijg jij (en eventueel je buren) een melding via de app of sirene. Met een meldkamer-abonnement van € 15 tot € 30 per maand wordt alarm ook doorgemeld als jij niet bereikbaar bent, en kan er een surveillant worden gestuurd.",
      },
      {
        v: "Bedraad of draadloos alarm?",
        a: "Draadloos is sneller geplaatst en ideaal in bestaande woningen, met batterijen die jaren meegaan. Bedraad is onderhoudsarm en storingsongevoelig, maar vraagt meer montagewerk. Een hybride systeem combineert beide en is vaak de beste keuze bij een verbouwing.",
      },
      {
        v: "Krijg ik korting op mijn verzekering?",
        a: "Dat kan. Sommige verzekeraars geven korting op de inboedelverzekering bij een gecertificeerd alarmsysteem of bij een woning met het Politiekeurmerk Veilig Wonen. Vraag dit vooraf na bij je eigen verzekeraar, want de voorwaarden verschillen.",
      },
      {
        v: "Wat is het Politiekeurmerk Veilig Wonen?",
        a: "Een keurmerk voor woningen die aan eisen voldoen op het gebied van inbraakpreventie, zoals goed hang- en sluitwerk en verlichting. Een alarmsysteem kan daar onderdeel van uitmaken. De installateur kan je vertellen wat voor jouw woning zinvol is.",
      },
    ],
  },
  {
    slug: "intercom-videodeurbel",
    naam: "Intercom of videodeurbel installeren",
    h1: "Videodeurbel of intercom laten installeren",
    prijs: "€ 150 tot € 1.500",
    van: 150,
    tot: 1500,
    gemiddeld: 600,
    uurtarief: "€ 55 tot € 85 per uur",
    doorlooptijd: "1 tot 4 uur",
    kosten: [
      { post: "Videodeurbel (toestel)", van: 50, tot: 500, detail: "van eenvoudig tot uitgebreid met detectie" },
      { post: "Installatie videodeurbel", van: 75, tot: 200, detail: "aansluiten op bestaande beltrafo of accu" },
      { post: "Video-intercomset", van: 250, tot: 1000, detail: "met binnenstation, voor woning of appartement" },
      { post: "Installatie intercom", van: 150, tot: 500, detail: "inclusief bekabeling en afmontage" },
      { post: "Cloudopslag", van: 3, tot: 10, detail: "per maand, voor het terugkijken van opnames" },
    ],
    kort: "Zien wie er voor de deur staat, van een videodeurbel tot een complete intercom.",
    intro:
      "Met een videodeurbel of intercom zie en spreek je bezoekers voordat je opendoet, waar je ook bent. De installateur controleert of je bestaande deurbelbedrading en beltrafo geschikt zijn, kiest een model op accu of bedrading en zorgt dat de app en meldingen goed werken. Voor appartementen kan een video-intercom op de bestaande installatie van de VvE worden aangesloten.",
    werk: [
      "Controleren van bestaande deurbelbedrading en beltrafo",
      "Monteren van de videodeurbel of buitenpost",
      "Plaatsen van het binnenstation of inrichten van de app",
      "Testen van beeld, geluid en deuropener",
      "Advies over wifi-bereik en privacy-instellingen",
    ],
    faq: [
      {
        v: "Kan mijn bestaande deurbelbedrading gebruikt worden?",
        a: "Vaak wel. Veel videodeurbellen werken op de bestaande beltrafo, mits die genoeg spanning levert. De installateur meet dat na en vervangt de trafo alleen als het nodig is.",
      },
      {
        v: "Videodeurbel op accu of bedraad?",
        a: "Op accu plaats je overal, zonder kabels, maar je laadt de accu om de paar maanden op. Bedraad is zorgeloos en altijd actief, maar vereist bestaande belbedrading of een nieuwe kabel. Bij voldoende wifi-bereik bij de voordeur werken beide even goed.",
      },
      {
        v: "Mag mijn videodeurbel de straat filmen?",
        a: "Nee, net als bij camerabeveiliging geldt de privacywet (AVG): richt de camera zoveel mogelijk op je eigen terrein en voordeur. Veel modellen hebben privacyzones waarmee je de openbare weg en het terrein van buren afdekt in beeld.",
      },
      {
        v: "Kan een intercom in een appartement?",
        a: "Ja. Bestaat er al een gedeelde intercominstallatie, dan stem je aanpassingen af met de VvE. De installateur kan vaak een video-intercom op de bestaande bedrading aansluiten, of een eigen videodeurbel plaatsen als dat mag.",
      },
    ],
  },
];

/** Extra, stadsspecifieke inhoud voor plaatsen waar we dieper op ingaan. */
export type PlaatsLokaal = {
  titel?: string;
  beschrijving?: string;
  intro: string[];
  wijkTekst: { wijk: string; tekst: string }[];
  omliggend: string;
  topKlussen: { dienstSlug: string; tekst: string }[];
  faq: { v: string; a: string }[];
};

export type Plaats = {
  slug: string;
  naam: string;
  provincie: string;
  inwoners: string;
  woningbeeld: string;
  wijken: string[];
  lokaal?: PlaatsLokaal;
};

export const plaatsen: Plaats[] = [
  { slug: "amsterdam", naam: "Amsterdam", provincie: "Noord-Holland", inwoners: "circa 930.000", woningbeeld: "veel vooroorlogse grachten- en portiekwoningen met verouderde bedrading en krappe meterkasten", wijken: ["De Pijp", "Oud-West", "Noord", "IJburg"] },
  { slug: "rotterdam", naam: "Rotterdam", provincie: "Zuid-Holland", inwoners: "circa 660.000", woningbeeld: "een mix van naoorlogse portiekflats en nieuwbouw op de Kop van Zuid", wijken: ["Kralingen", "Delfshaven", "Hillegersberg", "Feijenoord"] },
  { slug: "den-haag", naam: "Den Haag", provincie: "Zuid-Holland", inwoners: "circa 560.000", woningbeeld: "herenhuizen uit het interbellum naast moderne appartementen", wijken: ["Scheveningen", "Bezuidenhout", "Loosduinen", "Ypenburg"] },
  { slug: "utrecht", naam: "Utrecht", provincie: "Utrecht", inwoners: "circa 375.000", woningbeeld: "jaren-30 woningen in de wijken rond het centrum en nieuwbouw in Leidsche Rijn", wijken: ["Lombok", "Wittevrouwen", "Leidsche Rijn", "Overvecht"] },
  { slug: "eindhoven", naam: "Eindhoven", provincie: "Noord-Brabant", inwoners: "circa 245.000", woningbeeld: "veel jaren-50 en -60 woningen die toe zijn aan een nieuwe groepenkast", wijken: ["Strijp", "Woensel", "Stratum", "Meerhoven"] },
  { slug: "groningen", naam: "Groningen", provincie: "Groningen", inwoners: "circa 240.000", woningbeeld: "studentenpanden en oudere woningen waar vaak extra groepen nodig zijn", wijken: ["Schilderswijk", "Helpman", "Paddepoel", "Beijum"] },
  { slug: "tilburg", naam: "Tilburg", provincie: "Noord-Brabant", inwoners: "circa 225.000", woningbeeld: "ruime rijwoningen en voormalige fabriekspanden die verbouwd worden", wijken: ["Oud-Noord", "Reeshof", "Berkel-Enschot", "Blaak"] },
  { slug: "almere", naam: "Almere", provincie: "Flevoland", inwoners: "circa 220.000", woningbeeld: "jonge woningvoorraad waar vooral uitbreiding voor laadpalen en zonnepanelen speelt", wijken: ["Almere Stad", "Haven", "Buiten", "Poort"] },
  { slug: "breda", naam: "Breda", provincie: "Noord-Brabant", inwoners: "circa 185.000", woningbeeld: "vooroorlogse woningen in de singels en nieuwbouw aan de randen", wijken: ["Ginneken", "Haagse Beemden", "Prinsenbeek", "Teteringen"] },
  { slug: "nijmegen", naam: "Nijmegen", provincie: "Gelderland", inwoners: "circa 180.000", woningbeeld: "oude stadswoningen en wederopbouwpanden met verouderde installaties", wijken: ["Nijmegen-Oost", "Hatert", "Lent", "Dukenburg"] },
  { slug: "apeldoorn", naam: "Apeldoorn", provincie: "Gelderland", inwoners: "circa 165.000", woningbeeld: "veel vrijstaande woningen op ruime kavels met buitenverlichting en bijgebouwen", wijken: ["Ugchelen", "De Maten", "Zevenhuizen", "Osseveld"] },
  { slug: "haarlem", naam: "Haarlem", provincie: "Noord-Holland", inwoners: "circa 165.000", woningbeeld: "monumentale panden waar elektra zorgvuldig moet worden weggewerkt", wijken: ["Haarlem-Noord", "Schalkwijk", "Zijlweg", "Centrum"] },
  { slug: "arnhem", naam: "Arnhem", provincie: "Gelderland", inwoners: "circa 165.000", woningbeeld: "hellend gelegen woningen met kelders en oudere meterkasten", wijken: ["Klarendal", "Presikhaaf", "Schuytgraaf", "Velp-grens"] },
  { slug: "enschede", naam: "Enschede", provincie: "Overijssel", inwoners: "circa 160.000", woningbeeld: "ruime naoorlogse woningen en verbouwde fabrieks- en studentenpanden", wijken: ["Roombeek", "Glanerbrug", "Boswinkel", "Stadsveld"] },
  { slug: "amersfoort", naam: "Amersfoort", provincie: "Utrecht", inwoners: "circa 160.000", woningbeeld: "Vathorst-nieuwbouw naast een historische binnenstad met beschermde panden", wijken: ["Vathorst", "Soesterkwartier", "Kattenbroek", "Schothorst"] },
  { slug: "zaanstad", naam: "Zaanstad", provincie: "Noord-Holland", inwoners: "circa 160.000", woningbeeld: "houten Zaanse woningen waar bedrading extra aandacht vraagt", wijken: ["Zaandam", "Krommenie", "Wormerveer", "Assendelft"] },
  { slug: "s-hertogenbosch", naam: "'s-Hertogenbosch", provincie: "Noord-Brabant", inwoners: "circa 160.000", woningbeeld: "binnenstadspanden en groeiwijken met moderne installatievragen", wijken: ["Empel", "Rosmalen", "De Groote Wielen", "Muntel"] },
  { slug: "zwolle", naam: "Zwolle", provincie: "Overijssel", inwoners: "circa 135.000", woningbeeld: "veel eengezinswoningen uit de jaren 70 en 80 met groeiende stroomvraag", wijken: ["Assendorp", "Stadshagen", "Berkum", "Holtenbroek"] },
  { slug: "leiden", naam: "Leiden", provincie: "Zuid-Holland", inwoners: "circa 130.000", woningbeeld: "smalle historische panden waar leidingwerk creatief moet worden opgelost", wijken: ["Leiden-Noord", "Merenwijk", "Stevenshof", "Professorenwijk"] },
  { slug: "maastricht", naam: "Maastricht", provincie: "Limburg", inwoners: "circa 125.000", woningbeeld: "monumentale mergelpanden en appartementen met gedeelde meterkasten", wijken: ["Wyck", "Heer", "Céramique", "Sint-Pieter"] },
  { slug: "dordrecht", naam: "Dordrecht", provincie: "Zuid-Holland", inwoners: "circa 120.000", woningbeeld: "historische binnenstadswoningen naast naoorlogse wijken", wijken: ["Krispijn", "Dubbeldam", "Sterrenburg", "Stadspolders"] },
  { slug: "ede", naam: "Ede", provincie: "Gelderland", inwoners: "circa 120.000", woningbeeld: "ruime woningen aan de rand van de Veluwe met veel buitenaansluitingen", wijken: ["Ede-Zuid", "Veldhuizen", "Kernhem", "Bennekom-grens"] },
  { slug: "alkmaar", naam: "Alkmaar", provincie: "Noord-Holland", inwoners: "circa 110.000", woningbeeld: "oude binnenstad met beschermde gevels en nieuwbouw aan de Overdie", wijken: ["Overdie", "Huiswaard", "De Mare", "Oudorp"] },
  { slug: "delft", naam: "Delft", provincie: "Zuid-Holland", inwoners: "circa 105.000", woningbeeld: "veel verkamerde en oudere panden waar groepen snel te zwaar belast raken", wijken: ["Voorhof", "Tanthof", "Hof van Delft", "Wippolder"] },
  { slug: "venlo", naam: "Venlo", provincie: "Limburg", inwoners: "circa 102.000", woningbeeld: "naoorlogse woningen en bedrijfspanden met zwaardere krachtstroomvragen", wijken: ["Blerick", "Tegelen", "Belfeld", "Venlo-Zuid"] },
];

export const dienstBySlug = (slug: string) => diensten.find((d) => d.slug === slug);
export const plaatsBySlug = (slug: string): Plaats | undefined => {
  const plaats = plaatsen.find((p) => p.slug === slug);
  if (!plaats) return undefined;
  const lokaal = lokaleTeksten[plaats.slug];
  return lokaal ? { ...plaats, lokaal } : plaats;
};

/**
 * Prijsniveau per plaats ten opzichte van het landelijk gemiddelde (1,00).
 * Gebaseerd op de gangbare uurtarieven en voorrijkosten in de regio.
 */
export const prijsindex: Record<string, number> = {
  amsterdam: 1.14,
  haarlem: 1.1,
  utrecht: 1.09,
  "den-haag": 1.07,
  amstelveen: 1.12,
  leiden: 1.06,
  delft: 1.05,
  zaanstad: 1.05,
  alkmaar: 1.02,
  rotterdam: 1.04,
  dordrecht: 1.0,
  amersfoort: 1.03,
  almere: 1.0,
  eindhoven: 1.01,
  breda: 0.99,
  tilburg: 0.97,
  "s-hertogenbosch": 0.99,
  nijmegen: 0.98,
  arnhem: 0.97,
  apeldoorn: 0.96,
  ede: 0.97,
  zwolle: 0.96,
  enschede: 0.93,
  groningen: 0.94,
  maastricht: 0.95,
  venlo: 0.93,
};

export const plaatsIndex = (slug: string) => prijsindex[slug] ?? 1;

const afronden = (bedrag: number) => {
  if (bedrag >= 500) return Math.round(bedrag / 25) * 25;
  if (bedrag >= 100) return Math.round(bedrag / 5) * 5;
  return Math.round(bedrag);
};

/** Richtprijs van een dienst omgerekend naar het prijsniveau van een plaats. */
export const lokaalBedrag = (bedrag: number, plaatsSlug: string) =>
  afronden(bedrag * plaatsIndex(plaatsSlug));

export const lokaalBereik = (dienst: Dienst, plaatsSlug: string) =>
  bereik(lokaalBedrag(dienst.van, plaatsSlug), lokaalBedrag(dienst.tot, plaatsSlug));

/** Gangbare voorrijkosten in een plaats. */
export const voorrijkosten = (plaatsSlug: string) =>
  bereik(afronden(25 * plaatsIndex(plaatsSlug)), afronden(55 * plaatsIndex(plaatsSlug)));

export const SITE = "https://elektricienoffertes.com";

/** Plaatsen in de Randstad; deze worden dagelijks op indexatie gecontroleerd. */
export const randstadSlugs = [
  "amsterdam",
  "rotterdam",
  "den-haag",
  "utrecht",
  "haarlem",
  "leiden",
  "delft",
  "amersfoort",
  "almere",
  "zaanstad",
  "alkmaar",
  "dordrecht",
];

export const isRandstad = (slug: string) => randstadSlugs.includes(slug);

/** Veelgestelde vragen over een elektricien in een specifieke plaats. */
export const plaatsFaq = (plaats: Plaats): { v: string; a: string }[] => {
  const uur = `${euro(lokaalBedrag(55, plaats.slug))} tot ${euro(lokaalBedrag(85, plaats.slug))}`;
  const groepenkast = diensten.find((d) => d.slug.includes("groepenkast")) ?? diensten[0]!;
  const laadpaal = diensten.find((d) => d.slug.includes("laadpaal")) ?? diensten[0]!;
  const storing = diensten.find((d) => d.slug.includes("storing")) ?? diensten[0]!;
  return [
    {
      v: `Wat kost een elektricien in ${plaats.naam} per uur?`,
      a: `Een erkende elektricien in ${plaats.naam} rekent gemiddeld ${uur} per uur, plus ${voorrijkosten(plaats.slug)} voorrijkosten. Een klein karwei van een half uur tot een uur komt daarmee meestal uit op ${euro(lokaalBedrag(80, plaats.slug))} tot ${euro(lokaalBedrag(150, plaats.slug))}.`,
    },
    {
      v: `Wat kost het vervangen van een groepenkast in ${plaats.naam}?`,
      a: `Reken in ${plaats.naam} op ${lokaalBereik(groepenkast, plaats.slug)}, afhankelijk van het aantal groepen en de staat van de bestaande bedrading. Gemiddeld betaal je ${euro(lokaalBedrag(groepenkast.gemiddeld, plaats.slug))}.`,
    },
    {
      v: `Hoe snel kan een elektricien in ${plaats.naam} bij een storing komen?`,
      a: `Bij een storing reageren de aangesloten elektriciens in ${plaats.naam} meestal dezelfde dag of de volgende werkdag. Het oplossen van een storing kost doorgaans ${lokaalBereik(storing, plaats.slug)}.`,
    },
    {
      v: `Kan ik in ${plaats.naam} ook een laadpaal laten plaatsen?`,
      a: `Ja. Voor het plaatsen en aansluiten van een laadpaal in ${plaats.naam} reken je op ${lokaalBereik(laadpaal, plaats.slug)}, inclusief een aparte groep en aardlekbeveiliging. In wijken als ${plaats.wijken.slice(0, 2).join(" en ")} gebeurt dit veel.`,
    },
    {
      v: `Zijn de elektriciens in ${plaats.naam} erkend en verzekerd?`,
      a: `Alle vakmensen die via offerto offertes uitbrengen in ${plaats.naam} en omgeving zijn ingeschreven bij de Kamer van Koophandel, werken volgens NEN 1010 en zijn bedrijfsaansprakelijk verzekerd.`,
    },
    ...(plaats.lokaal?.faq ?? []),
  ];
};

/** Uitgebreide, stadsspecifieke inhoud. Alleen ingevuld voor plaatsen met eigen teksten. */
export const lokaleTeksten: Record<string, PlaatsLokaal> = {
  groningen: {
    titel: "Elektricien Groningen: kosten, tarieven en offertes 2026 | offerto",
    beschrijving:
      "Elektricien nodig in Groningen? Uurtarief € 50 tot € 80, voorrijkosten € 25 tot € 50. Groepenkast verzwaren, storing verhelpen, laadpaal of extra groepen in studentenpanden. Vergelijk gratis tot 5 offertes.",
    intro: [
      "Groningen heeft een woningvoorraad die elektrisch zwaar wordt belast. In de Schilderswijk, de Oosterpoort en rond het Noorderplantsoen staan veel vooroorlogse panden die in de loop der jaren zijn opgedeeld in kamers en studio's. Die panden hebben vaak nog een groepenkast met drie of vier groepen, terwijl er per verdieping een keuken, wasmachine en soms een eigen boiler hangt. Een elektricien in Groningen verzwaart daarom veel groepenkasten, plaatst extra aardlekschakelaars en trekt nieuwe groepen naar boven.",
      "Daarnaast speelt verduurzaming. Woningen in Helpman, Beijum en Paddepoel schakelen over op inductie, warmtepompen en zonnepanelen, en dat vraagt om een zwaardere aansluiting en soms om drie fasen. Ook het aansluiten van een laadpaal op eigen terrein komt in de buitenwijken steeds vaker voor. Omdat het prijsniveau in Groningen iets onder het landelijk gemiddelde ligt, betaal je hier doorgaans minder dan in de Randstad voor hetzelfde werk.",
    ],
    wijkTekst: [
      {
        wijk: "Schilderswijk",
        tekst:
          "Veel verkamerde vooroorlogse panden: extra groepen, aardlekbeveiliging per verdieping en het vervangen van oude stoppenkasten.",
      },
      {
        wijk: "Helpman",
        tekst:
          "Ruimere jaren-30 woningen waar renovaties spelen: nieuwe bedrading, inbouwspots en een groepenkast die geschikt is voor inductie en warmtepomp.",
      },
      {
        wijk: "Paddepoel",
        tekst:
          "Naoorlogse flats en eengezinswoningen met beperkte meterkastruimte, waar verzwaring en het wegwerken van leidingen om maatwerk vragen.",
      },
      {
        wijk: "Beijum",
        tekst:
          "Woningen met eigen parkeerplaats of oprit: laadpalen, buitenverlichting en aparte groepen voor de schuur.",
      },
    ],
    omliggend:
      "De aangesloten elektriciens werken ook in Haren, Zuidhorn, Leek, Winsum, Ten Boer en Hoogezand-Sappemeer.",
    topKlussen: [
      {
        dienstSlug: "groepenkast",
        tekst:
          "De meest aangevraagde klus in Groningen. Oude kasten met stoppen voldoen niet meer aan NEN 1010 en missen aardlekbeveiliging.",
      },
      {
        dienstSlug: "elektra-aanleggen",
        tekst:
          "Extra groepen en stopcontacten in verkamerde panden, vaak in combinatie met een verbouwing van keuken of badkamer.",
      },
      {
        dienstSlug: "storing-verhelpen",
        tekst:
          "Een aardlek die steeds uitvalt, meestal door een oude wasmachine, vocht in een buitenaansluiting of beschadigde bedrading.",
      },
      {
        dienstSlug: "laadpaal-installeren",
        tekst:
          "In de buitenwijken en omliggende dorpen, waar een eigen oprit of parkeerplaats beschikbaar is.",
      },
    ],
    faq: [
      {
        v: "Wat kost extra groepen bijplaatsen in een studentenpand in Groningen?",
        a: "Een extra groep bijplaatsen kost in Groningen gemiddeld € 150 tot € 300 per groep, inclusief materiaal en arbeid. Moet de hele groepenkast worden vervangen omdat er geen ruimte meer is, dan reken je op € 700 tot € 1.700 afhankelijk van het aantal groepen.",
      },
      {
        v: "Mag ik als huurder in Groningen zelf een elektricien inschakelen?",
        a: "Voor kleine aanpassingen zoals een extra stopcontact mag dat meestal met schriftelijke toestemming van de verhuurder. Werk aan de groepenkast, de meterkast of de vaste installatie valt onder het onderhoud van de verhuurder; vraag dus altijd eerst toestemming en laat het werk vastleggen in een rapport.",
      },
      {
        v: "Is een keuring verplicht bij verkamering in Groningen?",
        a: "Voor een omzettingsvergunning voor kamerverhuur vraagt de gemeente Groningen om een veilige installatie. Een NEN 1010-inspectierapport van een erkende elektricien kost doorgaans € 200 tot € 450 en voorkomt problemen bij de aanvraag en bij de verzekering.",
      },
      {
        v: "Kan ik in Groningen overstappen op drie fasen voor een warmtepomp?",
        a: "Ja. De elektricien maakt de groepenkast geschikt voor drie fasen en de netbeheerder verzwaart de aansluiting. Het elektrawerk kost meestal € 400 tot € 900; de verzwaring door de netbeheerder wordt apart in rekening gebracht.",
      },
    ],
  },
  apeldoorn: {
    titel: "Elektricien Apeldoorn: kosten, tarieven en offertes 2026 | offerto",
    beschrijving:
      "Elektricien nodig in Apeldoorn? Uurtarief € 50 tot € 80, voorrijkosten € 25 tot € 55. Laadpaal op eigen oprit, buitenverlichting, schuurgroep of groepenkast vervangen. Vergelijk gratis tot 5 offertes.",
    intro: [
      "Apeldoorn heeft veel vrijstaande en twee-onder-een-kapwoningen op ruime kavels, met een oprit, een tuin en vaak een schuur of garage. Daardoor gaat het elektrawerk hier vaker over buiten dan binnen: een aparte groep naar de schuur, buitenverlichting met bewegingsmelder, een buitenstopcontact bij het terras of een aansluiting voor een poort of tuinhuis. Grondkabel trekken over een lange oprit is in Apeldoorn een standaardklus.",
      "Ook laadpalen en zonnepanelen zijn hier veelgevraagd. De grote dakvlakken lenen zich goed voor een flinke installatie, en omdat vrijwel iedereen op eigen terrein parkeert, kan een laadpaal zonder gemeentelijke aanvraag geplaatst worden. Wel vraagt dat om een aparte groep met aardlekbeveiliging type A of B. Het prijsniveau in Apeldoorn ligt iets onder het landelijk gemiddelde, wat de kosten gunstiger maakt dan in de Randstad.",
    ],
    wijkTekst: [
      {
        wijk: "Ugchelen",
        tekst:
          "Vrijstaande woningen aan de bosrand: lange grondkabels naar bijgebouwen, tuinverlichting en aansluitingen voor een poort of pomp.",
      },
      {
        wijk: "De Maten",
        tekst:
          "Jaren-70 en -80 eengezinswoningen waar de groepenkast wordt verzwaard voor inductie, zonnepanelen en een laadpaal.",
      },
      {
        wijk: "Zevenhuizen",
        tekst:
          "Oudere woningen en appartementen waar oude bedrading en ontbrekende aardlekbeveiliging het meeste werk vormen.",
      },
      {
        wijk: "Osseveld",
        tekst:
          "Ruime nieuwere woningen met eigen oprit: laadpalen, buitenstopcontacten en extra groepen voor garage of thuiskantoor.",
      },
    ],
    omliggend:
      "De aangesloten elektriciens werken ook in Beekbergen, Vaassen, Twello, Epe, Deventer en Zutphen.",
    topKlussen: [
      {
        dienstSlug: "laadpaal-installeren",
        tekst:
          "Dé Apeldoornse klus: laden op eigen oprit, inclusief aparte groep, aardlekbeveiliging en soms een lange kabelroute.",
      },
      {
        dienstSlug: "verlichting-aanleggen",
        tekst:
          "Buiten- en tuinverlichting met bewegingsmelder of schemerschakelaar, vaak in combinatie met inbouwspots binnen.",
      },
      {
        dienstSlug: "elektra-aanleggen",
        tekst:
          "Een aparte groep en grondkabel naar schuur, garage of tuinhuis, plus buitenstopcontacten bij het terras.",
      },
      {
        dienstSlug: "groepenkast",
        tekst:
          "Verzwaring van de kast wanneer zonnepanelen, warmtepomp en laadpaal samen te veel vragen van de bestaande installatie.",
      },
    ],
    faq: [
      {
        v: "Wat kost een laadpaal op eigen oprit in Apeldoorn?",
        a: "Reken op € 900 tot € 1.900 inclusief laadpaal, aparte groep en aardlekbeveiliging. Ligt de oprit ver van de meterkast, dan komt daar grondwerk en kabel bij: gemiddeld € 15 tot € 30 per strekkende meter.",
      },
      {
        v: "Wat kost een aparte groep naar de schuur of garage?",
        a: "Een aparte groep met grondkabel naar een bijgebouw kost in Apeldoorn meestal € 350 tot € 800, afhankelijk van de afstand, of de sleuf al open ligt en of er een kleine verdeelkast in het bijgebouw komt.",
      },
      {
        v: "Heb ik een vergunning nodig voor buitenverlichting in Apeldoorn?",
        a: "Voor verlichting op eigen terrein is geen vergunning nodig. Voor een lichtmast aan de openbare weg of bij een monument gelden wel regels; de elektricien zorgt in alle gevallen voor spatwaterdichte armaturen en een aparte, aardlekbeveiligde groep.",
      },
      {
        v: "Moet mijn groepenkast mee bij zonnepanelen in Apeldoorn?",
        a: "Vaak wel. De omvormer hoort op een eigen groep met aardlekbeveiliging. Bij een oude kast zonder ruimte betekent dat een verzwaring van € 700 tot € 1.700. Bij een moderne kast volstaat een extra groep van € 150 tot € 300.",
      },
    ],
  },
};
